import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { usd } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { StatCard } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Building2, Users, DollarSign, Landmark, CalendarClock } from "lucide-react";
import { fmtDate } from "@/components/DatePicker";
import { AnomalyAlerts } from "@/components/AnomalyAlerts";
import { PrenoteReminderBanner } from "@/components/PrenoteReminderBanner";
import { TaxPendingBanner } from "@/components/TaxPendingBanner";
import { BureauComplianceCard } from "@/components/BureauComplianceCard";
import { FollowUpInbox } from "@/components/FollowUpInbox";
import { ReplyInbox } from "@/components/ReplyInbox";
import { OverdueDepositAlert } from "@/components/OverdueDepositAlert";
import { LateDepositTrends } from "@/components/LateDepositTrends";
import { ClientHealthScores } from "@/components/ClientHealthScores";
import { PendingFixReviews } from "@/components/PendingFixReviews";
import { FollowUpReminders } from "@/components/FollowUpReminders";
import { AddonRevenueCard } from "@/components/AddonRevenueCard";
import { useTileColors } from "@/lib/useTileColors";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area, Cell,
} from "recharts";

export default function BureauDashboard() {
  const tc = useTileColors(null);
  const [data, setData] = useState(null);
  const [paydays, setPaydays] = useState([]);
  const { selectCompany } = useCompany();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/bureau").then((r) => setData(r.data)).catch(() => {});
    api.get("/payroll/upcoming-paydays").then((r) => setPaydays(r.data.slice(0, 3))).catch(() => {});
  }, []);

  if (!data) return <div className="text-muted-foreground">Loading overview…</div>;

  const openClient = (id) => { selectCompany(id); navigate("/employees"); };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="bureau-dashboard">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Bureau Overview</h1>
        <p className="text-sm text-muted-foreground mt-1">Consolidated view across all client companies.</p>
      </div>
      <OverdueDepositAlert />
      <PrenoteReminderBanner />
      <TaxPendingBanner />

      <div data-testid="bureau-metrics-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <StatCard testid="metric-clients" label="Client Companies" value={data.total_clients} icon={Building2} color={tc.tile1} sub="Active tenants" />
        <StatCard testid="metric-employees" label="Total Employees" value={data.total_employees} icon={Users} color={tc.tile2} accent="text-emerald-600" sub="Across all clients" />
        <StatCard testid="metric-processed" label="Net Payroll Processed" value={usd(data.total_processed)} icon={DollarSign} color={tc.tile3} accent="text-blue-600" sub="Lifetime" />
        <StatCard testid="metric-tax" label="Taxes Withheld" value={usd(data.total_tax)} icon={Landmark} color={tc.tile4} accent="text-amber-600" sub="Fed / State / FICA" />
      </div>

      <AnomalyAlerts />
      <LateDepositTrends />
      <ClientHealthScores />
      <BureauComplianceCard />
      <div className="grid lg:grid-cols-2 gap-4"><FollowUpInbox params={{}} /><ReplyInbox params={{}} /></div>
      <PendingFixReviews />
      <FollowUpReminders />

      {paydays.length > 0 && (
        <Card className="p-4 sm:p-5" data-testid="upcoming-paydays-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold font-display flex items-center gap-2"><CalendarClock className="w-4 h-4 text-primary" /> Next paydays</h3>
            <button data-testid="paydays-view-all" onClick={() => navigate("/check-printing")} className="text-xs text-primary hover:underline">View all</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            {paydays.map((p) => {
              const days = Math.round((new Date(p.pay_date) - new Date(new Date().toISOString().slice(0, 10))) / 86400000);
              return (
                <button key={p.company_id} data-testid={`payday-widget-${p.company_id}`} onClick={() => navigate("/payroll")} className="text-left rounded-xl border px-4 py-3 hover:bg-accent transition-colors">
                  <div className="flex items-center justify-between gap-2"><span className="font-medium truncate">{p.company_name}</span><span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${days <= 2 ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{days <= 0 ? "today" : `in ${days}d`}</span></div>
                  <div className="font-mono-num text-xl font-semibold mt-1">{fmtDate(p.pay_date)}</div>
                  <div className="text-xs text-muted-foreground font-mono-num">{fmtDate(p.period_start)} → {fmtDate(p.period_end)} · <span className="capitalize font-sans">{p.pay_schedule}</span></div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5">
          <h3 className="font-semibold font-display mb-4">Monthly Gross Payroll Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.trend}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => usd(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Area type="monotone" dataKey="amount" stroke="#2563EB" strokeWidth={2.5} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4 sm:p-5">
          <h3 className="font-semibold font-display mb-4">Payroll by Client</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.per_company} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v) => usd(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Bar dataKey="payroll" radius={[0, 6, 6, 0]}>
                {data.per_company.map((c, i) => (<Cell key={i} fill={c.color || "#2563EB"} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <AddonRevenueCard />

      <Card className="p-0 overflow-hidden">
        <div className="p-5 border-b border-border">
          <h3 className="font-semibold font-display">Client Companies</h3>
        </div>
        <div className="divide-y divide-border">
          {data.per_company.map((c) => (
            <button
              key={c.id}
              data-testid={`client-row-${c.id}`}
              onClick={() => openClient(c.id)}
              className="w-full flex items-center gap-4 p-4 hover:bg-accent/60 transition-colors text-left"
            >
              <span className="text-2xl w-11 h-11 grid place-items-center rounded-lg" style={{ background: `${c.color}18` }}>{c.logo}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.industry}</div>
              </div>
              <div className="text-right hidden sm:block">
                <div className="text-xs text-muted-foreground">Employees</div>
                <div className="font-mono-num font-semibold">{c.employees}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Gross Payroll</div>
                <div className="font-mono-num font-semibold">{usd(c.payroll)}</div>
              </div>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
